/**
 * TrainingArticle — /training/:slug
 * ════════════════════════════════
 * Full article page with rich content, SEO, and "Book a Session" CTA.
 */
import React, { useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useContent } from '../context/ContentContext';
import { useSEO, jsonLd, articleJsonLd, trainingBreadcrumb } from '../utils/seo';
import Navbar from '../components/Navbar';
import './TrainingArticle.css';

export default function TrainingArticle({ onSignIn }) {
  const { slug }    = useParams();
  const navigate    = useNavigate();
  const { getArticleBySlug, getPublishedArticles, CATEGORIES } = useContent();

  const article  = getArticleBySlug(slug);

  useSEO({
    title:       article?.title,
    description: article?.excerpt,
    type:        'article',
    canonical:   article ? `/training/${article.slug}` : '/training',
    jsonLd:      article ? jsonLd.graph(
      articleJsonLd(article),
      trainingBreadcrumb(article)
    ) : undefined,
  });
  const cat      = article ? CATEGORIES.find(c => c.id === article.category) : null;

  // Related articles (same category, excluding current)
  const related  = article
    ? getPublishedArticles(article.category)
        .filter(a => a.slug !== slug)
        .slice(0, 3)
    : [];

  useEffect(() => {
    if (!article) navigate('/training', { replace: true });
  }, [article, navigate]);

  if (!article) return null;

  return (
    <>
      <Navbar onSignIn={onSignIn} />

      <div className="taa-page">
        <div className="taa-layout">

          {/* ── Main Article Column ──────────────────────── */}
          <article className="taa-main">

            {/* Breadcrumb */}
            <nav className="taa-breadcrumb">
              <Link to="/">Home</Link>
              <span>›</span>
              <Link to="/training">Training</Link>
              {cat && <><span>›</span><span style={{ color: cat.color }}>{cat.icon} {cat.label}</span></>}
            </nav>

            {/* Article Header */}
            <header className="taa-header">
              <span className="taa-cat-badge" style={{ color: cat?.color, background: `${cat?.color}18` }}>
                {cat?.icon} {cat?.label}
              </span>
              <h1 className="taa-title">{article.title}</h1>
              <p className="taa-excerpt">{article.excerpt}</p>
              <div className="taa-meta-row">
                <span className="taa-meta-item">
                  <span>✍️</span> DevBrains Team
                </span>
                <span className="taa-meta-sep">·</span>
                <span className="taa-meta-item">
                  <span>📅</span>
                  {new Date(article.publishedAt).toLocaleDateString('en-IN', {
                    day:'2-digit', month:'long', year:'numeric'
                  })}
                </span>
                <span className="taa-meta-sep">·</span>
                <span className="taa-meta-item">
                  <span>⏱</span> {article.readTimeMins} min read
                </span>
              </div>
            </header>

            {/* Cover emoji banner */}
            <div className="taa-cover" style={{ background: `${cat?.color || '#1DB954'}12` }}>
              <span className="taa-cover-emoji">{article.emoji || '📄'}</span>
            </div>

            {/* Article Body */}
            <div
              className="taa-body"
              dangerouslySetInnerHTML={{ __html: article.body }}
            />

            {/* Share row */}
            <div className="taa-share">
              <span className="taa-share-label">Share this guide</span>
              <div className="taa-share-btns">
                {[
                  {
                    label: 'LinkedIn',
                    bg: '#0A66C2',
                    url: `https://www.linkedin.com/sharing/share-offsite/?url=https://devbrains.in/training/${article.slug}`,
                  },
                  {
                    label: 'Twitter / X',
                    bg: '#000',
                    url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=https://devbrains.in/training/${article.slug}`,
                  },
                  {
                    label: 'WhatsApp',
                    bg: '#25D366',
                    url: `https://wa.me/?text=${encodeURIComponent(article.title + ' — https://devbrains.in/training/' + article.slug)}`,
                  },
                ].map(s => (
                  <a
                    key={s.label}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="taa-share-btn"
                    style={{ background: s.bg }}
                  >
                    {s.label}
                  </a>
                ))}
              </div>
            </div>

            {/* Back link */}
            <Link to="/training" className="taa-back">← Back to all guides</Link>
          </article>

          {/* ── Sidebar ──────────────────────────────────── */}
          <aside className="taa-sidebar">

            {/* Book a Session CTA */}
            <div className="taa-cta-card">
              <div className="taa-cta-top">
                <div className="taa-cta-icon">🚀</div>
                <div className="taa-cta-headline">Ready to put this into practice?</div>
                <p className="taa-cta-sub">
                  Book a 1:1 session with a verified mentor and apply what you've learned today.
                </p>
              </div>
              <div className="taa-cta-stats">
                {[['2,400+', 'Verified Mentors'], ['⭐ 4.9', 'Avg Rating'], ['Free', 'First Session']].map(([n, l]) => (
                  <div key={l} className="taa-cta-stat">
                    <strong>{n}</strong><span>{l}</span>
                  </div>
                ))}
              </div>
              <Link to="/mentors" className="taa-cta-btn">Find My Mentor →</Link>
              <div className="taa-cta-trust">🔒 Secure payment · Free first session</div>
            </div>

            {/* Related articles */}
            {related.length > 0 && (
              <div className="taa-related">
                <div className="taa-related-label">More Guides</div>
                {related.map(a => {
                  const rc = CATEGORIES.find(c => c.id === a.category);
                  return (
                    <Link key={a.id} to={`/training/${a.slug}`} className="taa-related-item">
                      <span className="taa-related-emoji">{a.emoji || '📄'}</span>
                      <div>
                        <div className="taa-related-title">{a.title}</div>
                        <div className="taa-related-meta">
                          <span style={{ color: rc?.color }}>{rc?.icon} {rc?.label}</span>
                          <span> · {a.readTimeMins} min</span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
                <Link to="/training" className="taa-related-all">View all guides →</Link>
              </div>
            )}

            {/* Also check out news */}
            <div className="taa-also">
              <div className="taa-also-label">Also from DevBrains</div>
              <Link to="/news" className="taa-also-link">
                <span>📢</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '.84rem', color: '#111827' }}>Platform News</div>
                  <div style={{ fontSize: '.72rem', color: '#9ca3af', marginTop: 2 }}>Latest updates & announcements</div>
                </div>
                <span style={{ marginLeft: 'auto', color: '#9ca3af' }}>→</span>
              </Link>
            </div>

          </aside>
        </div>
      </div>
    </>
  );
}
